import { defineStore } from 'pinia'
import { ref } from 'vue'

export type NotificationType = 'info' | 'success' | 'warning' | 'error'

export interface Notification {
  id: string
  type: NotificationType
  title: string
  message: string
  /** ms on screen. `0` = persistent (the owner removes it). Omitted = per-type default. */
  duration?: number
}

/** Default on-screen time (ms) when the caller does not pass a `duration`. */
export const DEFAULT_NOTIFICATION_DURATION: Record<NotificationType, number> = {
  error: 8000,
  warning: 6000,
  success: 4000,
  info: 4000,
}

/** After a pause (hover/focus) the toast gets at least this long again, so it can be read. */
const MIN_RESUME_MS = 2000

/** Safeguards: a runaway caller must not flood the stack nor render a wall of text. */
const MAX_STORED_NOTIFICATIONS = 20
const MAX_TITLE_LENGTH = 120
const MAX_MESSAGE_LENGTH = 300
const GENERIC_ERROR_TEXT = 'Ocurrió un error inesperado.'

/** Keys probed (in order) on an object/Error passed where a string was expected. */
const TEXT_KEYS = ['message', 'detail', 'error', 'title'] as const
/** Same, on `response.data` of an axios-like error. */
const RESPONSE_DATA_KEYS = ['message', 'detail', 'error'] as const

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

const firstString = (source: unknown, keys: readonly string[]): string | null => {
  if (!isRecord(source)) return null
  for (const key of keys) {
    const candidate = source[key]
    if (typeof candidate === 'string' && candidate.trim() !== '') return candidate
  }
  return null
}

/**
 * Coerce whatever a caller handed us (a `catch (e)` value, an untyped axios payload…) into a
 * displayable string. Never yields "[object Object]": unusable objects become a generic Spanish
 * text for error/warning toasts and an empty string for the rest.
 */
const normalizeText = (value: unknown, type: NotificationType): string => {
  if (typeof value === 'string') return value
  if (value === null || value === undefined) return ''
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
    return String(value)
  }
  try {
    const responseData = isRecord(value) && isRecord(value.response) ? value.response.data : null
    const found = firstString(value, TEXT_KEYS) ?? firstString(responseData, RESPONSE_DATA_KEYS)
    if (found !== null) return found
  } catch {
    // A hostile getter/proxy must not break the caller's error handling: use the fallback.
  }
  return type === 'error' || type === 'warning' ? GENERIC_ERROR_TEXT : ''
}

const truncate = (text: string, max: number): string =>
  text.length > max ? `${text.slice(0, max - 1)}…` : text

interface Countdown {
  handle: ReturnType<typeof setTimeout> | null
  remaining: number
  startedAt: number
}

export const useNotificationStore = defineStore('notification', () => {
  const notifications = ref<Notification[]>([])
  // Plain Map (not reactive): timers are bookkeeping, not view state.
  const countdowns = new Map<string, Countdown>()

  const startCountdown = (id: string, ms: number) => {
    countdowns.set(id, {
      handle: setTimeout(() => remove(id), ms),
      remaining: ms,
      startedAt: Date.now(),
    })
  }

  /**
   * Make room for one more: evict the oldest non-persistent notification (duration !== 0) first,
   * and a persistent one only when every stored notification is persistent. `remove` clears the
   * countdown (running or paused) so evictions never leave a timer behind.
   */
  const makeRoom = () => {
    while (notifications.value.length >= MAX_STORED_NOTIFICATIONS) {
      const victim = notifications.value.find((n) => n.duration !== 0) ?? notifications.value[0]
      remove(victim.id)
    }
  }

  const add = (notification: Omit<Notification, 'id'>) => {
    const id = Math.random().toString(36).substring(2, 9)
    const newNotification = {
      ...notification,
      id,
      title: truncate(normalizeText(notification.title, notification.type), MAX_TITLE_LENGTH),
      message: truncate(normalizeText(notification.message, notification.type), MAX_MESSAGE_LENGTH),
    }
    // Evict BEFORE pushing so the notification being added is never the one dropped.
    makeRoom()
    notifications.value.push(newNotification)

    if (notification.duration !== 0) {
      const ms =
        typeof notification.duration === 'number' && notification.duration > 0
          ? notification.duration
          : DEFAULT_NOTIFICATION_DURATION[notification.type]
      startCountdown(id, ms)
    }

    return id
  }

  const remove = (id: string) => {
    const countdown = countdowns.get(id)
    if (countdown) {
      if (countdown.handle !== null) clearTimeout(countdown.handle)
      countdowns.delete(id)
    }
    const index = notifications.value.findIndex(n => n.id === id)
    if (index > -1) {
      notifications.value.splice(index, 1)
    }
  }

  /** Freeze the auto-dismiss countdown (e.g. while the pointer/focus is on the toast). Idempotent. */
  const pause = (id: string) => {
    const countdown = countdowns.get(id)
    if (!countdown || countdown.handle === null) return
    clearTimeout(countdown.handle)
    countdown.handle = null
    countdown.remaining = Math.max(0, countdown.remaining - (Date.now() - countdown.startedAt))
  }

  /** Restart a paused countdown with the time left (never less than 2 s). Idempotent. */
  const resume = (id: string) => {
    const countdown = countdowns.get(id)
    if (!countdown || countdown.handle !== null) return
    const ms = Math.max(countdown.remaining, MIN_RESUME_MS)
    countdown.remaining = ms
    countdown.startedAt = Date.now()
    countdown.handle = setTimeout(() => remove(id), ms)
  }

  const info = (title: string, message: string, duration?: number) => {
    return add({ type: 'info', title, message, duration })
  }

  const success = (title: string, message: string, duration?: number) => {
    return add({ type: 'success', title, message, duration })
  }

  const warning = (title: string, message: string, duration?: number) => {
    return add({ type: 'warning', title, message, duration })
  }

  const error = (title: string, message: string, duration?: number) => {
    return add({ type: 'error', title, message, duration })
  }

  return {
    notifications,
    add,
    remove,
    pause,
    resume,
    info,
    success,
    warning,
    error
  }
})
